import React from 'react'
import { bool, node, object, oneOf, oneOfType, string, func, number } from 'prop-types'
import classnames from 'classnames'

import Avatar from 'components/atoms/Avatar'
import Button from 'components/atoms/Button'
import CardImage from 'components/atoms/CardImage'
import CardContent from 'components/atoms/CardContent'
import CardActions from 'components/atoms/CardActions'
import Ellipsis from 'components/atoms/Ellipsis'
import ScholarshipIcon from 'components/atoms/SVGIcons/Scholarship'
import getMajorIcon from 'utils/getMajorIcon'

import LikeButton from 'components/molecules/LikeButton'
import AuthenticatedLink from 'components/molecules/AuthenticatedLink'

import Relazsizes from 'relazsizes'
import { SVGIcon } from 'components/qstrap'
import API from 'utils/API'
import csrfToken from 'utils/csrfToken'
import config from 'config'

const { assets_url: assetsUrl } = config

const WrapperLink = ({
  authRequired,
  recordLead,
  leadUrl,
  source,
  sourceDetail,
  title,
  linkRel,
  linkTarget,
  href,
  children,
  disabled,
  dataTestId,
}) => {
  const trackAction = () => {
    const options = {
      headers: {
        'X-CSRF-Token': csrfToken(),
      },
    }
    const params = {
      source,
      source_detail: sourceDetail,
    } || {}

    API.put(
      leadUrl,
      params,
      options
    )
      .then(() => {
        window.location = href
      })
      .catch(() => {
        // TODO Show alert message somewhere in the page
      })
  }

  const handleClick = (e) => {
    e && e.preventDefault()
    if (recordLead) {
      trackAction()
      return true
    }
    return true
  }

  if (disabled) {
    return (
      <div className="card-link-wrapper">
        <span>{children}</span>
      </div>
    )
  }
  return (
    <div className="card-link-wrapper">
      {
        authRequired || recordLead ?
          <AuthenticatedLink
            href={href}
            onClick={handleClick}
            successUrl={href}
            linkProps={{ rel: linkRel, target: linkTarget }}
            dataTestId={dataTestId}
          >
            {children}
          </AuthenticatedLink> :
          <a
            href={href}
            title={title}
            rel={linkRel}
            target={linkTarget}
            data-testid={dataTestId}
          >
            {children}
          </a>
      }
      <style jsx>
        {`
          .card-link-wrapper {
            :global(a) {
              text-decoration: unset;
              color: unset;
              &:hover {
                text-decoration: none;
              }
            }
          }
        `}
      </style>
    </div>
  )
}

WrapperLink.propTypes = {
  recordLead: bool,
  leadUrl: string,
  source: string,
  sourceDetail: object,
  title: string,
  linkRel: string,
  linkTarget: string,
  href: string,
  authRequired: bool,
  disabled: bool,
  dataTestId: string,
}

const CampusPerfectCard = ({
  authRequired,
  className,
  category,
  description,
  footerContent,
  href,
  icon,
  kind,
  leadUrl,
  leadSource,
  leadSourceDetail,
  likeable,
  likeStatus,
  likeTarget,
  linkRel,
  logo,
  openNewTab,
  recordLead,
  thumbnail,
  title,
  withFooter,
  onClick,
  disabled,
  grayscale,
  thumbnailOverlay,
  capacity,
  interest,
  competitiveLevel,
  flag,
  withFlag,
  shortImage,
  major,
  dataTestId,
}) => {
  const classNameVal = classnames('campus-perfect-card', className)

  const kindColor = {
    schools: logo ? '#fff' : '#ebeef3',
    studyFields: '#00c2c9',
    professions: '#2440a4',
    scholarships: logo ? '#fff' : '#00c2c9',
  }

  const renderLogo = () => {
    if (logo) {
      return (
        <Relazsizes
          className="campus-perfect-card__logo-img"
          dataSrc={logo}
          alt={title}
        />
      )
    }
    if (kind === 'studyFields' && icon !== 'common-course') {
      return getMajorIcon(icon)
    }
    if (icon) {
      return <SVGIcon name={icon} size="md" />
    }
    if (kind === 'scholarships') {
      return (
        <ScholarshipIcon fill="#FFF"/>
      )
    }
    return ''
  }

  return (
    <div className={classNameVal} onClick={onClick}>
      {
        flag &&
        <div className="campus-perfect-card__flag">
          {flag}
        </div>
      }
      {
        likeable &&
        <div className="campus-perfect-card__like-wrapper">
          <LikeButton
            active={likeStatus}
            kind={kind}
            targetId={likeTarget}
            dataTestId={dataTestId && dataTestId.replace('card', 'love-btn')}
          />
        </div>
      }
      <WrapperLink
        href={href}
        title={title}
        leadUrl={leadUrl}
        linkRel={linkRel}
        linkTarget={openNewTab ? '_blank' : ''}
        source={leadSource}
        sourceDetail={leadSourceDetail}
        recordLead={recordLead}
        authRequired={authRequired}
        disabled={disabled}
        dataTestId={dataTestId}
      >
        <CardImage
          imgSrc={thumbnail}
          imgAlt={title}
          height={shortImage ? '140px' : '160px'}
          disabled={disabled || grayscale}
          overlay={thumbnailOverlay}
        />
        <CardContent className="campus-perfect-card__content">
          {
            category &&
            <p className="campus-perfect-card__category">
              { category }
            </p>
          }
          <div className="campus-perfect-card__identity">
            {
              kind !== 'partners' &&
              <Avatar
                className="campus-perfect-card__logo"
                size={48}
                rounded={!logo}
                backgroundColor={kindColor[kind]}
                disabled={disabled || grayscale}
              >
                {renderLogo()}
              </Avatar>
            }
            <Ellipsis
              component="h4"
              className="campus-perfect-card__title"
              height={48}
              text={title}
            />
          </div>
          {
            withFlag && (category === 'Negeri') ?
              <div className="campus-perfect-card__recommendation">
                {major}
                <div className="campus-perfect-card__row">
                  <p>Daya Tampung {new Date().getFullYear()}:</p>
                  <p>{capacity}</p>
                </div>
                <div className="campus-perfect-card__row">
                  <p>Peminat {new Date().getFullYear() - 1}:</p>
                  <p>{interest}</p>
                </div>
                <div className="campus-perfect-card__row">
                  <p>Persaingan:</p>
                  <p>{competitiveLevel}%</p>
                </div>
              </div> :
              <div className="campus-perfect-card__desc">
                <Ellipsis
                  text={description}
                  markdown
                />
              </div>
          }
        </CardContent>
        {
          withFooter &&
          <CardActions>
            { footerContent }
            <Button component="button" variant="link" className={`campus-perfect-card__see-more ${disabled || grayscale ? 'disabled' : ' '}`}>
              Selengkapnya
              <SVGIcon name="common-next" />
            </Button>
          </CardActions>
        }
      </WrapperLink>
      <style jsx>
        {`
          .campus-perfect-card {
            position: relative;
            padding: 1rem;
            box-shadow: 0 1px 3px 0 #a3b4ca;
            background-color: #ffffff;
            border-radius: 4px;
            margin-top: 0;

            &__like-wrapper {
              position: absolute;
              top: 1rem;
              right: 1rem;
            }
            &__category {
              font-size: 12px;
              color: #a3b4ca;
              margin-bottom: 8px;
            }
            &__identity {
              display: flex;
              align-items: center;
            }
            &__desc {
              margin: 8px 0;
              :global(span) {
                font-size: 14px;
                color: #7d92a9;
                line-height: 1.71;
              }
            }
            &__recommendation {
              margin-top: 8px;
            }
            &__row {
              display: flex;
              justify-content: space-between;
              :global(p) {
                margin-bottom: 0;
                &:first-child {
                  color: #7d92a9;
                }
              }
            }
            :global(&__title) {
              color: #485c77;
              line-height: 1.5;
              font-size: 16px;
              font-weight: bold;
            }
            :global(&__logo) {
              margin-right: 16px;
              padding: ${logo ? '0' : '8px'} !important;
            }
            :global(&__see-more) {
              border: none;
              padding: 0;
              margin-left: auto;
              font-size: 14px;
              font-weight: bold;
              background: none;
              color: ${kind === 'professions' ? kindColor.professions : kindColor.studyFields};
            }
            :global(&__see-more.disabled) {
              color: #7d92a9;
            }
            @media screen and (min-width: 768px) {
              &__desc {
                height: 69px;
              }
              &__flag {
                background-image: url('${assetsUrl}/assets/img/paperplane/result/flag.svg');
                background-size: contain;
                background-repeat: no-repeat;
                position: absolute;
                left: -2%;
                top: -4%;
                padding: 8px 36px 21px 16px;
                color: #fff;
                font-size: 16px;
                font-weight: bold;
              }
            }
            @media screen and (max-width: 767px) {
              &__flag {
                display: none;
              }
            }
          }
        `}
      </style>
    </div>
  )
}

CampusPerfectCard.propTypes = {
  authRequired: bool,
  category: oneOfType([string, node]),
  className: string,
  description: string,
  footerContent: node,
  href: string,
  icon: string,
  kind: oneOf([
    'professions',
    'schools',
    'studyFields',
    'partners',
    'tests',
    'scholarships',
  ]),
  leadUrl: string,
  leadSource: string,
  leadSourceDetail: object,
  likeable: bool,
  likeStatus: bool,
  likeTarget: string,
  linkRel: string,
  logo: string,
  openNewTab: bool,
  recordLead: bool,
  thumbnail: string,
  title: string,
  withFooter: bool,
  onClick: func,
  disabled: bool,
  grayscale: bool,
  thumbnailOverlay: string,
  major: string,
  capacity: number,
  interest: number,
  competitiveLevel: number,
  flag: string,
  withFlag: bool,
  shortImage: bool,
  dataTestId: string,
}

export { WrapperLink }

export default CampusPerfectCard
